import { useState } from 'react';
import {
  DEFAULT_SUPPLIER_CATEGORIES,
  type Supplier,
  type SupplierInput,
} from '../../lib/suppliersApi';
import { lookupAnafByCui } from '../../lib/anafApi';
import { ApiError } from '../../lib/api';
import { Select } from '../ui/Select';
import { useCompanies } from '../../hooks/useCompanies';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { useSupplierCategories } from '../../hooks/useSuppliers';
import { useActiveCompanyId } from '../../store/useCompanyStore';

// Create/edit modal for a supplier (CLE-151). Reused by the list page (create),
// the detail header (edit), and the bank-statement reconcile page (quick-add,
// pre-filled from the statement counterparty). Flat form mirroring the backend
// zod schema.

interface SupplierFormModalProps {
  title: string;
  initial?: Supplier;
  /** Pre-fill values for the quick-add flow (statement counterparty name/IBAN). */
  prefill?: Partial<SupplierInput>;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: SupplierInput) => void | Promise<void>;
}

const FIELDS: { key: keyof SupplierInput; label: string; type?: string; full?: boolean }[] = [
  { key: 'name', label: 'Nume *', full: true },
  { key: 'cui', label: 'CUI / Cod TVA' },
  { key: 'iban', label: 'IBAN' },
  { key: 'contactPerson', label: 'Persoană de contact' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Telefon' },
  { key: 'address', label: 'Adresă', full: true },
];

export function SupplierFormModal({
  title,
  initial,
  prefill,
  submitting,
  onClose,
  onSubmit,
}: SupplierFormModalProps) {
  const [form, setForm] = useState<SupplierInput>(() => ({
    name: initial?.name ?? prefill?.name ?? '',
    cui: initial?.cui ?? prefill?.cui ?? '',
    iban: initial?.iban ?? prefill?.iban ?? '',
    contactPerson: initial?.contactPerson ?? prefill?.contactPerson ?? '',
    email: initial?.email ?? prefill?.email ?? '',
    phone: initial?.phone ?? prefill?.phone ?? '',
    address: initial?.address ?? prefill?.address ?? '',
    category: initial?.category ?? prefill?.category ?? 'other',
    isRecurring: initial?.isRecurring ?? prefill?.isRecurring ?? false,
    notes: initial?.notes ?? prefill?.notes ?? '',
    isActive: initial?.isActive ?? true,
  }));
  const [error, setError] = useState<string | null>(null);

  // Settings-managed category nomenclature (fallback: seed defaults).
  const { data: categoryDefs } = useSupplierCategories();
  const categories = categoryDefs?.length ? categoryDefs : DEFAULT_SUPPLIER_CATEGORIES;

  // Multi-company: firm tag ('' = default firm). Hidden on single-firm
  // installs; new suppliers default to the firm from the global switcher.
  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;
  const activeCompanyId = useActiveCompanyId();
  const [companyId, setCompanyId] = useState<string>(
    initial ? (initial.companyId ?? '') : (activeCompanyId ?? ''),
  );

  // Default expense category preselected on bank-statement reconciliation
  // ('' = none). The generic income/expense defaults are excluded — they are
  // the fallback categories every line already gets at import.
  // Categories this firm sees (shared + its own); '' = the default firm.
  const { data: expenseCategories } = useExpenseCategories(
    false,
    companyId || companies?.find((c) => c.isDefault)?.id,
  );
  const expenseCategoryOptions = (expenseCategories ?? []).filter(
    (c) => c.code !== 'income' && c.code !== 'expense',
  );
  const [expenseCategoryId, setExpenseCategoryId] = useState<string>(
    initial?.expenseCategoryId ?? '',
  );

  // ANAF CUI autocomplete (CLE-122) — only offered when creating a new supplier.
  const isCreate = !initial;
  const [cuiInput, setCuiInput] = useState(prefill?.cui ?? '');
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafError, setAnafError] = useState<string | null>(null);
  const [anafWarning, setAnafWarning] = useState<string | null>(null);
  const [anafSuccess, setAnafSuccess] = useState<string | null>(null);

  function set(key: keyof SupplierInput, value: string | boolean) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleAnafLookup() {
    const cui = cuiInput.trim();
    setAnafError(null);
    setAnafWarning(null);
    setAnafSuccess(null);
    const digits = cui.replace(/^ro\s*/i, '').replace(/\s+/g, '');
    if (!/^\d{2,10}$/.test(digits)) {
      setAnafError('Cod TVA invalid. Introdu doar cifre (ex. 47847495).');
      return;
    }
    setAnafLoading(true);
    try {
      const result = await lookupAnafByCui(cui);
      if (!result.found || !result.prefill) {
        setAnafError('Codul TVA nu a fost găsit în ANAF. Completează manual.');
        return;
      }
      const p = result.prefill;
      setForm((prev) => ({
        ...prev,
        name: p.name || prev.name,
        cui: p.cifCui || prev.cui,
        address: p.address || prev.address,
        phone: p.phone ?? prev.phone,
        notes: p.notes ?? prev.notes,
      }));
      if (result.warning) setAnafWarning(result.warning);
      else setAnafSuccess('Date preluate din ANAF. Verifică și salvează.');
    } catch (err) {
      if (err instanceof ApiError) setAnafError(err.message);
      else setAnafError('Serviciul ANAF nu răspunde. Completează manual.');
    } finally {
      setAnafLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Numele este obligatoriu.');
      return;
    }
    // Strip empty strings so optional fields stay null server-side; keep
    // booleans and the category enum verbatim.
    const cleaned: Record<string, unknown> = {
      name: form.name.trim(),
      category: form.category ?? 'other',
      isRecurring: form.isRecurring ?? false,
    };
    for (const [k, v] of Object.entries(form)) {
      if (k === 'name' || k === 'category' || k === 'isRecurring') continue;
      if (typeof v === 'boolean') {
        cleaned[k] = v;
      } else if (typeof v === 'string' && v.trim()) {
        cleaned[k] = v.trim();
      }
    }
    // Firm tag: on edit, '' explicitly clears back to the default firm (null);
    // on create, only send it when a firm was actually chosen.
    if (multiFirm) {
      if (initial) cleaned.companyId = companyId === '' ? null : companyId;
      else if (companyId) cleaned.companyId = companyId;
    }
    // Default expense category: '' clears it (null server-side).
    cleaned.expenseCategoryId = expenseCategoryId === '' ? null : expenseCategoryId;
    try {
      await onSubmit(cleaned as unknown as SupplierInput);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A apărut o eroare.');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Închide"
            >
              ✕
            </button>
          </div>

          {isCreate && (
            <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Cod TVA — completare automată din ANAF
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cuiInput}
                  onChange={(e) => setCuiInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAnafLookup();
                    }
                  }}
                  placeholder="ex. 47847495 sau RO47847495"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={() => void handleAnafLookup()}
                  disabled={anafLoading}
                  className="inline-flex shrink-0 items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {anafLoading && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  Caută
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Introdu codul TVA și apasă Enter sau „Caută" pentru a precompleta datele.
              </p>
              {anafError && <p className="mt-2 text-sm text-red-600">{anafError}</p>}
              {anafWarning && (
                <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-sm text-amber-700">
                  {anafWarning}
                </p>
              )}
              {anafSuccess && <p className="mt-2 text-sm text-green-600">{anafSuccess}</p>}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                <label className="mb-1 block text-xs font-medium text-gray-600">{f.label}</label>
                <input
                  type={f.type ?? 'text'}
                  value={(form[f.key] as string) ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            ))}
            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">Categorie</span>
              <Select
                label="Categorie"
                hideLabel
                value={form.category ?? 'other'}
                onChange={(v) => set('category', v)}
                options={categories.map((c) => ({
                  value: c.code,
                  label: c.label,
                }))}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isRecurring ?? false}
                  onChange={(e) => set('isRecurring', e.target.checked)}
                />
                Furnizor recurent (potrivire automată la importurile viitoare)
              </label>
            </div>
            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">
                Categorie cheltuială implicită
              </span>
              <Select
                label="Categorie cheltuială implicită"
                hideLabel
                clearable
                value={expenseCategoryId}
                onChange={setExpenseCategoryId}
                placeholder="Fără categorie implicită"
                options={expenseCategoryOptions.map((c) => ({ value: c.id, label: c.name }))}
              />
              <p className="mt-1 text-xs text-gray-500">
                Preselectată automat la reconcilierea extraselor pentru plățile către acest
                furnizor.
              </p>
            </div>
            {multiFirm && companies && (
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-600">Firmă</span>
                <Select
                  label="Firmă"
                  hideLabel
                  clearable
                  value={companyId}
                  onChange={setCompanyId}
                  placeholder="Firma implicită"
                  options={companies.map((c) => ({
                    value: c.id,
                    label: c.displayName || c.legalName,
                  }))}
                />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">Notițe</label>
              <textarea
                value={form.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
                rows={3}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            {!isCreate && (
              <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={(e) => set('isActive', e.target.checked)}
                />
                Furnizor activ
              </label>
            )}
          </div>

          {error && <p className="px-5 pb-2 text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              Salvează
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
